import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { apiService } from '../services/api';
import { ActiveJob, TrackedJob } from '../types';

export interface JobContextValue {
  /** All jobs from the tracker — the single source of truth. */
  trackedJobs: Record<string, TrackedJob>;
  loading: boolean;
  error: string | null;
  refreshJobs: () => Promise<Record<string, TrackedJob>>;
  removeJob: (jobId: string) => void;

  // Legacy compat — derived from trackedJobs
  jobs: Record<string, ActiveJob>;
  upsertJob: (job: ActiveJob) => void;
  pollJob: (jobId: string) => Promise<ActiveJob>;
}

export const JobContext = createContext<JobContextValue | null>(null);

const TERMINAL_STATUSES = new Set(['DONE', 'FAILED', 'CANCELLED', 'UPLOAD_FAILED']);
const ACTIVE_STATUS_FILTER = ['UPLOADING', 'UPLOAD_FAILED', 'PENDING', 'RUNNING', 'CANCELLING', 'FAILED', 'CANCELLED', 'DONE'];
const REFRESH_INTERVAL_MS = 1500;  // smoother progress/ETA updates while a job runs

export const trackedJobToActiveJob = (job: TrackedJob): ActiveJob => ({
  jobId: job.job_id,
  status: job.status ?? 'PENDING',
  progress: typeof job.progress === 'number' && !Number.isNaN(job.progress) ? job.progress : 0,
  message: job.message ?? null,
  phase: job.phase ?? null,
  phaseProgress: typeof job.phase_progress === 'number' ? job.phase_progress : null,
  phaseCompleted: null,
  phaseTotal: null,
  steps: Array.isArray((job.payload as any)?.steps) ? ((job.payload as any).steps as string[]) : null,
  currentStep: typeof (job.payload as any)?.current_step === 'number' ? (job.payload as any).current_step : null,
  etaSeconds: typeof (job.payload as any)?.eta_seconds === 'number' ? (job.payload as any).eta_seconds : null,
  totalGaps: 0,
  finishedGaps: 0,
  error: job.error ?? null,
  result: (job.payload as any)?.result ?? null,
  cancelled: job.status === 'CANCELLED',
  jobType: job.job_type ?? null,
  datasetPath: job.dataset_path ?? null,
  metadataPath: job.metadata_path ?? null,
  pdfPath: null,
  createdAt: job.created_at ?? null,
  updatedAt: job.updated_at ?? job.created_at ?? null,
  caseId: job.case_id ?? (job.payload as any)?.case_id ?? null,
});

export const JobProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [trackedJobs, setTrackedJobs] = useState<Record<string, TrackedJob>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadCompleteRef = useRef<boolean>(false);

  const refreshJobs = useCallback(async (): Promise<Record<string, TrackedJob>> => {
    const shouldShowInitialLoading = !initialLoadCompleteRef.current;
    if (shouldShowInitialLoading) {
      setLoading(true);
    }
    try {
      const response = await apiService.fetchTrackedJobs({
        status: ACTIVE_STATUS_FILTER,
        order: 'queue',
      });
      const trackedMap: Record<string, TrackedJob> = {};
      response.jobs.forEach(job => {
        trackedMap[job.job_id] = job;
      });
      setTrackedJobs(prev => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(trackedMap);
        if (prevKeys.length !== nextKeys.length) return trackedMap;
        for (const key of nextKeys) {
          const pj = prev[key];
          if (!pj) return trackedMap;
          const nj = trackedMap[key];
          if (pj.status !== nj.status || pj.updated_at !== nj.updated_at) return trackedMap;
        }
        return prev;
      });
      setError(null);
      return trackedMap;
    } catch (err) {
      console.error('Failed to refresh tracked jobs', err);
      setError('Failed to refresh job status');
      return trackedJobsRef.current;
    } finally {
      if (shouldShowInitialLoading) {
        initialLoadCompleteRef.current = true;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void refreshJobs();
    const interval = window.setInterval(() => {
      void refreshJobs();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [refreshJobs]);

  const removeJob = useCallback((jobId: string) => {
    setTrackedJobs(prev => {
      if (!(jobId in prev)) return prev;
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
  }, []);

  // Derive ActiveJob map from trackedJobs for legacy consumers
  const jobs = useMemo(() => {
    const active: Record<string, ActiveJob> = {};
    Object.values(trackedJobs).forEach(tj => {
      const aj = trackedJobToActiveJob(tj);
      if (!TERMINAL_STATUSES.has(aj.status)) {
        active[aj.jobId] = aj;
      }
    });
    return active;
  }, [trackedJobs]);

  // No-op stubs for legacy interface — callers that used these should
  // just call refreshJobs() instead
  const upsertJob = useCallback((_job: ActiveJob) => {
    // No-op: tracker refresh is the only state source now
  }, []);

  const trackedJobsRef = useRef(trackedJobs);
  trackedJobsRef.current = trackedJobs;

  const pollJob = useCallback(
    async (jobId: string): Promise<ActiveJob> => {
      // Poll until the job reaches a terminal status, using the freshly
      // fetched data directly (not the ref, which lags by one render).
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const fresh = await refreshJobs();
        const tj = Object.values(fresh).find(j => j.job_id === jobId);
        if (tj && TERMINAL_STATUSES.has(tj.status ?? '')) {
          return trackedJobToActiveJob(tj);
        }
        await new Promise(resolve => setTimeout(resolve, REFRESH_INTERVAL_MS));
      }
    },
    [refreshJobs],
  );

  const value = useMemo<JobContextValue>(
    () => ({
      jobs,
      trackedJobs,
      loading,
      error,
      refreshJobs,
      upsertJob,
      removeJob,
      pollJob,
    }),
    [jobs, trackedJobs, loading, error, refreshJobs, upsertJob, removeJob, pollJob],
  );

  return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
};

export const useJobs = (): JobContextValue => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};
